export const INGEST_PHASES = [
    "queued",
    "transcoding",
    "ingesting",
    "publishing",
    "completed",
    "failed",
];
export const WAITING_PHASES = [
    "transcoding",
    "ingesting",
    "publishing",
];
export const PHASE_DEADLINES_MS = {
    transcoding: 4 * 60 * 60 * 1000,
    ingesting: 30 * 60 * 1000,
    publishing: 10 * 60 * 1000,
};
export const MAX_ATTEMPTS = 3;
function entering(phase, now) {
    const budget = PHASE_DEADLINES_MS[phase];
    return {
        phase,
        phaseStartedAt: now,
        attempts: 0,
        ...(budget
            ? { deadlineAt: new Date(Date.parse(now) + budget).toISOString() }
            : {}),
    };
}
function closing(state, now) {
    return {
        phase: state.phase,
        startedAt: state.phaseStartedAt,
        completedAt: now,
        ...(state.progress === undefined ? {} : { progress: state.progress }),
        ...(state.error === undefined ? {} : { error: state.error }),
    };
}
function failed(state, now, error, incident) {
    return {
        ...state,
        phase: "failed",
        phaseStartedAt: now,
        deadlineAt: undefined,
        error,
        completedAt: now,
        phaseLog: [...state.phaseLog, closing(state, now)],
        incidents: [...state.incidents, incident],
    };
}
export function advanceIngest(state, observation, now) {
    var _a, _b;
    if (state.phase === "completed" || state.phase === "failed")
        return state;
    if (state.deadlineAt && Date.parse(now) >= Date.parse(state.deadlineAt)) {
        return failed(state, now, `Timed out waiting in ${state.phase} after ` +
            `${Math.round(PHASE_DEADLINES_MS[state.phase] / 60000)} minutes.`, {
            code: "phase_timeout",
            phase: state.phase,
            occurredAt: now,
            attempt: state.attempts + 1,
            detail: state.error,
        });
    }
    switch (observation.kind) {
        case "nothing":
            return state;
        case "dispatched":
            if (state.phase !== "queued")
                return state;
            return {
                ...state,
                ...entering("transcoding", now),
                encoreJobId: observation.encoreJobId,
                encoreStatusUrl: observation.encoreStatusUrl,
                progress: 0,
                error: undefined,
                phaseLog: [...state.phaseLog, closing(state, now)],
            };
        case "dispatch_failed": {
            if (state.phase !== "queued")
                return state;
            const attempt = state.attempts + 1;
            const incident = {
                code: "dispatch_failed",
                phase: "queued",
                occurredAt: now,
                attempt,
                detail: observation.detail,
            };
            if (attempt >= MAX_ATTEMPTS) {
                return failed(state, now, `Could not hand the job to the transcoder after ${MAX_ATTEMPTS} attempts: ${observation.detail}`, incident);
            }
            return {
                ...state,
                attempts: attempt,
                error: `Transcoder dispatch failed: ${observation.detail}`,
                incidents: [...state.incidents, incident],
            };
        }
        case "transcode": {
            if (state.phase !== "transcoding")
                return state;
            if (observation.status === "failed") {
                return failed(state, now, (_a = observation.detail) !== null && _a !== void 0 ? _a : "The transcoder rejected this source.", {
                    code: "transcode_failed",
                    phase: "transcoding",
                    occurredAt: now,
                    attempt: state.attempts + 1,
                    detail: observation.detail,
                });
            }
            if (observation.status === "running") {
                return { ...state, progress: (_b = observation.progress) !== null && _b !== void 0 ? _b : state.progress };
            }
            return {
                ...state,
                ...entering("ingesting", now),
                progress: 100,
                error: undefined,
                phaseLog: [
                    ...state.phaseLog,
                    closing({ ...state, progress: 100 }, now),
                ],
            };
        }
        case "not_ingested_yet":
            return state;
        case "ingesting":
            return state.phase === "ingesting"
                ? { ...state, error: undefined }
                : state;
        case "ingested": {
            if (state.phase !== "ingesting")
                return state;
            if (!observation.dash && !observation.hls) {
                return { ...state, error: "Ready, but no playable URL was published." };
            }
            return {
                ...state,
                ...entering("publishing", now),
                manifestUrls: {
                    ...(observation.dash ? { dash: observation.dash } : {}),
                    ...(observation.hls ? { hls: observation.hls } : {}),
                },
                error: undefined,
                phaseLog: [...state.phaseLog, closing(state, now)],
            };
        }
        case "unreachable": {
            const occurrence = state.incidents.filter((i) => i.code === "upstream_unreachable" && i.phase === state.phase).length + 1;
            return {
                ...state,
                error: observation.detail,
                incidents: [
                    ...state.incidents,
                    {
                        code: "upstream_unreachable",
                        phase: state.phase,
                        occurredAt: now,
                        attempt: occurrence,
                        detail: observation.detail,
                    },
                ],
            };
        }
        case "published":
            if (state.phase !== "publishing")
                return state;
            return {
                ...state,
                phase: "completed",
                phaseStartedAt: now,
                deadlineAt: undefined,
                attempts: 0,
                progress: 100,
                error: undefined,
                completedAt: now,
                phaseLog: [...state.phaseLog, closing(state, now)],
            };
        case "publish_failed": {
            if (state.phase !== "publishing")
                return state;
            const attempt = state.attempts + 1;
            const incident = {
                code: "publish_failed",
                phase: "publishing",
                occurredAt: now,
                attempt,
                detail: observation.detail,
            };
            if (attempt >= MAX_ATTEMPTS) {
                return failed(state, now, `The catalogue would not accept the finished streams after ${MAX_ATTEMPTS} attempts: ${observation.detail}`, incident);
            }
            return {
                ...state,
                attempts: attempt,
                error: `Catalogue update failed: ${observation.detail}`,
                incidents: [...state.incidents, incident],
            };
        }
    }
}
export function newIngestJob(now) {
    return {
        phase: "queued",
        phaseStartedAt: now,
        attempts: 0,
        phaseLog: [],
        incidents: [],
    };
}
export function ingestStepState(job, step) {
    var _a;
    if (job.phase === "failed") {
        const failedIn = (_a = job.phaseLog[job.phaseLog.length - 1]) === null || _a === void 0 ? void 0 : _a.phase;
        if (step === failedIn)
            return "failed";
    }
    if (job.phaseLog.some((entry) => entry.phase === step))
        return "done";
    if (job.phase === step)
        return "current";
    if (job.phase === "completed")
        return "done";
    return "waiting";
}
const RETRY_RESUMES_AT = {
    queued: "queued",
    transcoding: "queued",
    ingesting: "ingesting",
    publishing: "publishing",
};
export function retryIngest(state, now) {
    var _a, _b;
    if (state.phase !== "failed")
        return state;
    const lastEntry = state.phaseLog[state.phaseLog.length - 1];
    const failedAt = (_a = lastEntry === null || lastEntry === void 0 ? void 0 : lastEntry.phase) !== null && _a !== void 0 ? _a : "queued";
    const resume = (_b = RETRY_RESUMES_AT[failedAt]) !== null && _b !== void 0 ? _b : "queued";
    const next = {
        ...state,
        ...entering(resume, now),
        phaseLog: [...state.phaseLog, closing(state, now)],
        error: undefined,
        progress: undefined,
        completedAt: undefined,
    };
    if (resume === "queued") {
        delete next.encoreJobId;
        delete next.encoreStatusUrl;
    }
    return next;
}
export function ingestSourceObjectName(assetId, extension) {
    const ext = extension.startsWith(".") ? extension : `.${extension}`;
    return `${assetId}${ext.toLowerCase()}`;
}
export const SUBTITLE_EXTENSIONS = ["srt", "vtt"];
export function ingestSubtitleObjectName(assetId, language, extension) {
    const ext = extension.replace(/^\./, "").toLowerCase();
    return `${assetId}/${language.toLowerCase()}.${ext}`;
}
